import { z } from 'zod';

import { otpErrorBodySchema } from './auth.schema';

/** How the API words an account that has used up its attempts. Only the agent
 *  can lift that, so offering a resend would send the director in a circle. */
const LOCKOUT_MESSAGE =
  /account has been locked|too many otp requests|too many invalid requests/i;

export const isLockoutMessage = (message: string | null): boolean =>
  message !== null && LOCKOUT_MESSAGE.test(message);

/** What `unwrap()` throws for an OTP call: the fetch error, plus the
 *  `Retry-After` header that `requestOtp` carries across. */
const thrownSchema = z.object({
  status: z.union([z.number(), z.string()]),
  data: z.unknown(),
  retryAfter: z.string().nullish(),
});

export interface AgentContact {
  email: string | null;
  phone: string | null;
}

export interface OtpFailure {
  isThrottled: boolean;
  message: string | null;
  retryAfterSec: number | null;
  /** Present when the API names who can unlock the account. */
  agentContact: AgentContact | null;
}

type OtpErrorBody = z.infer<typeof otpErrorBodySchema>;

const headerSeconds = (header: string | null | undefined): number | null => {
  const seconds = Number(header);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
};

const retryAfterOf = (body: OtpErrorBody, header: string | null | undefined): number | null =>
  body.details?.retryAfterSec ?? body.codeSpecificData?.retryAfterSec ?? headerSeconds(header);

const agentContactOf = (body: OtpErrorBody): AgentContact | null => {
  const email = body.codeSpecificData?.agentEmail ?? null;
  const phone = body.codeSpecificData?.agentPhone ?? null;
  return email === null && phone === null ? null : { email, phone };
};

/** Reads a failed OTP call. Tolerant throughout: a body that does not parse
 *  still leaves the screen with a failure to show, just with its own copy. */
export const readOtpFailure = (error: unknown): OtpFailure => {
  const thrown = thrownSchema.safeParse(error);
  if (!thrown.success) {
    return { isThrottled: false, message: null, retryAfterSec: null, agentContact: null };
  }

  const parsedBody = otpErrorBodySchema.safeParse(thrown.data.data);
  const body = parsedBody.success ? parsedBody.data : {};

  return {
    isThrottled: thrown.data.status === 429,
    message: body.message ?? body.error ?? null,
    retryAfterSec: retryAfterOf(body, thrown.data.retryAfter),
    agentContact: agentContactOf(body),
  };
};
